import {
  CheckCircle2,
  ChevronRight,
  Loader2,
  ShieldCheck,
  ShieldX,
  TriangleAlert,
  Wrench,
} from "lucide-react";
import { createContext, useContext, useState, type ReactNode } from "react";

import { Checkpoint, CheckpointIcon } from "../ai-elements/checkpoint";
import { Button } from "../ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { checkpointEvent } from "./checkpoint-part";
import { toolCallIdOf } from "./tool-answer";
import { splitToolOutput, type ToolImage } from "./tool-output";
import type {
  AnyToolPart,
  ApprovalCopy,
  RenderToolCard,
  RespondToApproval,
} from "./types";

/**
 * The host's hooks into tool rendering, kept in context so they reach the
 * deeply-nested part renderer without threading a prop through every message
 * and part in between. `ChatPane` provides it; nothing else needs to.
 */
interface ToolHost {
  respond: RespondToApproval | null;
  renderToolCard: RenderToolCard | null;
  approvalCopy: ((modelName: string, input: unknown) => ApprovalCopy) | null;
}

const ToolHostContext = createContext<ToolHost>({
  respond: null,
  renderToolCard: null,
  approvalCopy: null,
});

export function ToolHostProvider({
  respond,
  renderToolCard,
  approvalCopy,
  children,
}: Partial<Omit<ToolHost, "respond">> & {
  respond?: RespondToApproval;
  children: ReactNode;
}) {
  return (
    <ToolHostContext.Provider
      value={{
        respond: respond ?? null,
        renderToolCard: renderToolCard ?? null,
        approvalCopy: approvalCopy ?? null,
      }}
    >
      {children}
    </ToolHostContext.Provider>
  );
}

/**
 * The **model-facing** tool name, qualified — `heath__pixelpusher__reset`.
 *
 * Kept separate from {@link toolDisplayName} because that one shortens to the
 * last segment for readability, which destroys the routing identity: parsing an
 * App tool out of `reset` is impossible, so anything routing on the shortened
 * label silently treats every App tool as a core tool.
 */
export function toolModelName(part: AnyToolPart): string {
  // Dynamic tools carry their name; static tools are `tool-<name>`.
  return part.type === "dynamic-tool"
    ? part.toolName
    : part.type.slice("tool-".length);
}

export function toolDisplayName(part: AnyToolPart): string {
  const raw = toolModelName(part);
  // App tools are registry-id-qualified (`<publisher>__<name>__<tool>`); show
  // just the final tool segment so the UI reads nicely (`eval`, not
  // `heath__typescript-console__eval`). Split on the LAST `__` so the whole
  // `<publisher>__<name>__` prefix is dropped (and older `<name>__<tool>` names
  // read cleanly too). Core tools have no `__` and pass through.
  const i = raw.lastIndexOf("__");
  return i < 0 ? raw : raw.slice(i + 2);
}

/** A call's `description` input, shown beside its name (`eval` takes one). */
function Summary({ part }: { part: AnyToolPart }) {
  const input: unknown = "input" in part ? part.input : undefined;
  const text =
    input && typeof input === "object" && "description" in input
      ? input.description
      : undefined;
  return typeof text === "string" ? (
    <span className="min-w-0 truncate text-[11px] text-muted-foreground">
      {text}
    </span>
  ) : null;
}

/** A streaming tool's yields land as `output-available` results flagged
 *  preliminary: progress, not a settled call. */
const isPreliminary = (part: AnyToolPart): boolean =>
  part.state === "output-available" && part.preliminary === true;

/**
 * How far a streaming tool has got, off its latest preliminary yield
 * (`{ status, steps }`). A sub-agent like `delegate` runs for many minutes
 * behind one spinner, and a spinner alone can't tell "working" from "hung" —
 * the rising count can.
 */
export function toolProgress(
  part: AnyToolPart,
): { steps: number; status?: string } | null {
  if (!isPreliminary(part)) return null;
  const output = (part.output ?? {}) as Record<string, unknown>;
  if (typeof output.steps !== "number") return null;
  return {
    steps: output.steps,
    status: typeof output.status === "string" ? output.status : undefined,
  };
}

function ProgressPill({ part }: { part: AnyToolPart }) {
  const progress = toolProgress(part);
  if (!progress) return null;
  return (
    <span
      className="rounded bg-muted px-1 text-[10px] tabular-nums text-muted-foreground"
      title={progress.status}
    >
      {progress.steps === 1 ? "1 step" : `${progress.steps} steps`}
    </span>
  );
}

function StatusIcon({ part }: { part: AnyToolPart }) {
  const state = part.state;
  if (state === "output-available" && !isPreliminary(part)) {
    return <CheckCircle2 className="h-3 w-3 text-emerald-600" />;
  }
  if (state === "output-error") {
    return <TriangleAlert className="h-3 w-3 text-destructive" />;
  }
  // A member denied the approval — a settled outcome (the deliberate "no"),
  // not a failure and definitely not still-running.
  if (state === "output-denied") {
    return <ShieldX className="h-3 w-3 text-muted-foreground" />;
  }
  // The member said YES. `approval-responded` is where an approved call comes to
  // rest — the prompt build accepts it as resolved and no result is ever written
  // back onto this part, so a spinner here would run forever on a call that is
  // done.
  if (state === "approval-responded") {
    return <ShieldCheck className="h-3 w-3 text-muted-foreground" />;
  }
  return <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />;
}

/**
 * The settled status for a whole tool run, drawn as a stack of the terminal
 * outcomes that occurred: all-success reads as one green check, all-error as one
 * error mark, and a mix shows the error peeking out *behind* the green — a hint
 * that both live inside the group. Painted back-to-front via DOM order.
 */
function StatusStack({
  hasSuccess,
  hasError,
  hasDenied,
}: {
  hasSuccess: boolean;
  hasError: boolean;
  hasDenied?: boolean;
}) {
  if (hasSuccess && hasError) {
    return (
      <span
        className="relative inline-flex h-3 w-[18px] items-center"
        title="Some calls succeeded, some errored"
      >
        <TriangleAlert className="absolute right-0 h-3 w-3 text-destructive/70" />
        <CheckCircle2 className="absolute left-0 h-3 w-3 text-emerald-600" />
      </span>
    );
  }
  if (hasError) return <TriangleAlert className="h-3 w-3 text-destructive" />;
  // No success and no error but a member's "no" — the run reads as denied,
  // never as an all-green check.
  if (hasDenied && !hasSuccess) {
    return <ShieldX className="h-3 w-3 text-muted-foreground" />;
  }
  return <CheckCircle2 className="h-3 w-3 text-emerald-600" />;
}

function formatJson(value: unknown): string {
  if (value === undefined) return "";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/**
 * Collapses a run of consecutive tool calls (from one assistant turn, with no
 * intervening text/reasoning) into a single line showing only the *most recent*
 * call and its status. As new calls stream in, the line "replaces" itself with
 * the latest — so a long tool chain reads as one live status row instead of a
 * wall of pills. Expanding reveals the whole chain; each call inside is itself a
 * `ToolCallView` that expands to its input/output.
 *
 * A run of one has nothing to collapse, so it renders as a bare `ToolCallView`.
 */
export function ToolCallGroup({ parts }: { parts: AnyToolPart[] }) {
  const latest = parts[parts.length - 1];
  if (!latest) return null;

  if (parts.length === 1) {
    return <ToolCallView part={latest} />;
  }

  // The run is "settled" only once *every* call reaches a terminal state — a
  // single step can fan out parallel calls that finish out of order, so a
  // terminal tail doesn't imply the earlier calls are done. While in flight the
  // header tracks the most recent call that's still running (name + spinner), so
  // a live tool never reads as complete. Once settled, the last tool's name is a
  // misleading label for a chain of *different* tools, so summarize by count and
  // roll every call's outcome up into a single stacked status. A denied
  // approval (`output-denied`) is terminal too — the member's "no" settles the
  // call as surely as a result does.
  // `approval-responded` is terminal for the same reason `output-denied` is: the
  // member answered. It's also where an approved call RESTS in the persisted
  // transcript — the live stream shows the execution result, but the stored part
  // stays at the resolved approval, so treating it as in-flight made a finished
  // group flip from settled back to spinning once the turn ended and the message
  // was re-read.
  const isTerminal = (p: AnyToolPart) =>
    (p.state === "output-available" && !isPreliminary(p)) ||
    p.state === "output-error" ||
    p.state === "output-denied" ||
    p.state === "approval-responded";
  const settled = parts.every(isTerminal);
  const hasSuccess = parts.some((p) => p.state === "output-available");
  const hasError = parts.some((p) => p.state === "output-error");
  const hasDenied = parts.some((p) => p.state === "output-denied");
  const running = settled
    ? latest
    : (parts.findLast((p) => !isTerminal(p)) ?? latest);

  return (
    <Collapsible className="my-1 overflow-hidden rounded-md border border-border bg-background/60 text-xs">
      <CollapsibleTrigger
        className="group flex w-full items-center gap-1.5 px-2 py-1 text-left hover:bg-muted/60"
        title={`Show all ${parts.length} tool calls`}
      >
        <ChevronRight className="h-3 w-3 shrink-0 transition-transform group-data-[state=open]:rotate-90" />
        <Wrench className="h-3 w-3 shrink-0 text-muted-foreground" />
        {settled ? (
          <span className="text-[11px] font-medium text-muted-foreground">
            {parts.length} tool calls
          </span>
        ) : (
          <>
            <span className="font-mono text-[11px] font-medium">
              {toolDisplayName(running)}
            </span>
            <Summary part={running} />
            <span className="rounded bg-muted px-1 text-[10px] tabular-nums text-muted-foreground">
              {parts.length}
            </span>
            <ProgressPill part={running} />
          </>
        )}
        <span className="ml-auto flex items-center">
          {settled ? (
            <StatusStack
              hasSuccess={hasSuccess}
              hasError={hasError}
              hasDenied={hasDenied}
            />
          ) : (
            <StatusIcon part={running} />
          )}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-1 border-t border-border bg-muted/30 px-1.5 py-1">
        {parts.map((p, i) => (
          <ToolCallView key={i} part={p} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

export function ToolCallView({ part }: { part: AnyToolPart }) {
  const host = useContext(ToolHostContext);
  const name = toolDisplayName(part);
  const modelName = toolModelName(part);
  // The host's card wins outright: a parked App tool, a payment awaiting the
  // member's phone, a bespoke destructive confirmation. It is asked about every
  // part, and declining is `undefined`. Whatever it returns must be an ELEMENT,
  // not the result of calling a hook-bearing function inline — a host card that
  // needs hooks owns them in its own component, and falls back to the exported
  // {@link ToolCallRow} rather than trying to re-enter this seam.
  const hosted = host.renderToolCard?.({ part, name, modelName });
  if (hosted !== undefined) return <>{hosted}</>;
  // An open approval request takes over the row with a prominent card: it needs
  // a human decision, so it can't hide inside the collapsed tool line. Resolved
  // approvals fall through to the normal collapsible below (the settled call, or
  // its denial, reads like any other tool part).
  if (part.state === "approval-requested") {
    const copy = host.approvalCopy?.(modelName, part.input) ?? {
      title: `Allow ${name}?`,
      detail: approvalDetail(part.input),
      note: "The agent is waiting on your decision.",
    };
    return <ToolApprovalCard part={part} copy={copy} />;
  }
  // A settled checkpoint reads as a marker across the transcript: the project
  // moved as a whole, which is a different kind of event from a tool doing
  // something inside it.
  const checkpoint = checkpointEvent(part);
  if (checkpoint) {
    return (
      <Checkpoint className="py-1">
        <CheckpointIcon
          className={checkpoint.kind === "restore" ? "text-primary" : undefined}
        />
        <span className="truncate px-1 text-xs">{checkpoint.label}</span>
      </Checkpoint>
    );
  }
  return <ToolCallRow part={part} />;
}

/**
 * One tool call as a collapsible line: name + status, expanding to its input,
 * output, images, and error. This is the shell's plain rendering, with no host
 * seam in it — which is what makes it the safe fallback for a host card that
 * decided it has nothing special to show.
 */
export function ToolCallRow({
  part,
  redactOutput,
}: {
  part: AnyToolPart;
  /**
   * Mask parts of a result before display — a one-time card number the member
   * never needs to read. Display-layer only: the message part is untouched.
   */
  redactOutput?: (output: unknown) => unknown;
}) {
  const name = toolDisplayName(part);
  const errorText = part.state === "output-error" ? part.errorText : undefined;
  const raw = part.state === "output-available" ? part.output : undefined;
  const output = redactOutput ? redactOutput(raw) : raw;
  const { images, rest, text } = splitToolOutput(output);
  const input =
    part.state === "input-streaming" ||
    part.state === "input-available" ||
    part.state === "output-available" ||
    part.state === "output-error" ||
    // `approval-requested` is handled above by the card and narrowed out here.
    part.state === "approval-responded" ||
    part.state === "output-denied"
      ? part.input
      : undefined;

  // Whether the fields `splitToolOutput` couldn't read are a leftover rather
  // than the whole result — i.e. something else already says what happened.
  const leftover = images.length > 0 || !!text;

  return (
    // A result with an image opens itself: the picture IS the progress the
    // member is following (what the agent just looked at), and a disclosure they
    // have to click renders it invisible.
    <Collapsible
      defaultOpen={images.length > 0}
      className="my-1 overflow-hidden rounded-md border border-border bg-background/60 text-xs"
    >
      <CollapsibleTrigger className="group flex w-full items-center gap-1.5 px-2 py-1 text-left hover:bg-muted/60">
        <ChevronRight className="h-3 w-3 shrink-0 transition-transform group-data-[state=open]:rotate-90" />
        <Wrench className="h-3 w-3 shrink-0 text-muted-foreground" />
        <span className="font-mono text-[11px] font-medium">{name}</span>
        <Summary part={part} />
        <ProgressPill part={part} />
        <span className="ml-auto">
          <StatusIcon part={part} />
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2 border-t border-border bg-muted/30 px-2 py-1.5 font-mono text-[11px]">
        <Section label="input" content={formatJson(input)} />
        {part.state === "output-denied" ? (
          <Section label="result" content="A member denied this request." />
        ) : null}
        {errorText ? (
          <Section label="error" content={errorText} tone="error" />
        ) : null}
        {images.length > 0 ? (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
              output
            </div>
            <div className="mt-1 flex flex-wrap gap-2">
              {images.map((image, i) => (
                <ToolResultImage key={i} image={image} />
              ))}
            </div>
          </div>
        ) : null}
        {text ? (
          <Section
            label={images.length > 0 ? "output (text)" : "output"}
            content={text}
          />
        ) : null}
        {rest !== undefined ? (
          <Section
            label={leftover ? "output (data)" : "output"}
            content={formatJson(rest)}
            // Leftover JSON beside a picture or a summary is reference material,
            // not the result — it takes its own click. On its own it IS the
            // result, and hiding it behind a second disclosure buries it.
            disclosed={leftover}
          />
        ) : null}
      </CollapsibleContent>
    </Collapsible>
  );
}

/** The one field of a tool input a generic approval card can honestly show. */
function approvalDetail(input: unknown): string | undefined {
  const record = (input ?? {}) as Record<string, unknown>;
  for (const key of ["url", "path", "reason", "resource"]) {
    const value = record[key];
    if (typeof value === "string" && value) return value;
  }
  return undefined;
}

/**
 * The chrome every card that interrupts the transcript shares: an amber (or,
 * when `alarming`, a destructive) panel with a leading icon, a title, an
 * optional wrapped detail, and a row of actions.
 *
 * Exported because a host's own cards belong in the same visual language as the
 * shell's — a consent prompt and an approval prompt reading differently is a
 * bug, not a feature.
 */
export function ToolNoticeCard({
  icon,
  title,
  detail,
  note,
  notice,
  alarming = false,
  children,
}: {
  icon?: ReactNode;
  title: ReactNode;
  /** The agent's stated reason (or the target). Wraps, never truncates. */
  detail?: ReactNode;
  note?: ReactNode;
  /** A block between the detail and the actions — e.g. a safety warning. */
  notice?: ReactNode;
  alarming?: boolean;
  /** The actions row. */
  children?: ReactNode;
}) {
  return (
    <div
      className={
        alarming
          ? "my-1 overflow-hidden rounded-md border border-destructive/50 bg-destructive/5 text-xs"
          : "my-1 overflow-hidden rounded-md border border-amber-500/40 bg-amber-500/5 text-xs"
      }
    >
      <div className="flex items-start gap-2 px-3 py-2">
        {icon ? <span className="mt-0.5 shrink-0">{icon}</span> : null}
        <div className="min-w-0 flex-1">
          <div className="font-medium text-foreground">{title}</div>
          {detail ? (
            <div className="mt-0.5 break-words font-mono text-[11px] text-muted-foreground">
              {detail}
            </div>
          ) : null}
          {notice ? <div className="mt-1.5">{notice}</div> : null}
          {note ? (
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              {note}
            </div>
          ) : null}
          {children ? <div className="mt-2 flex gap-2">{children}</div> : null}
        </div>
      </div>
    </div>
  );
}

/** An affirmative control that replaces Approve — e.g. "Connect Google". */
export interface ApprovalPrimaryAction {
  label: string;
  icon?: ReactNode;
  busy?: boolean;
  /**
   * `destructive` when saying yes destroys something. The card's own
   * `alarming` tone colors the panel; this colors the button the member
   * actually clicks.
   */
  variant?: "default" | "destructive";
  /** Resolve true to answer the card approved; false leaves it open. */
  run: () => Promise<boolean>;
}

/**
 * The one-click escalation card for an open tool-approval request. Approve
 * answers the suspended call so it runs; Deny turns it into a tool-denied result
 * the model reasons about. A surface without a responder wired can't act, so it
 * degrades to a plain notice.
 *
 * A host that needs a different affirmative action (connect an account first) or
 * an extra warning above the buttons composes this rather than rebuilding it, so
 * every approval in the product has one shape.
 */
export function ToolApprovalCard({
  part,
  copy,
  notice,
  primaryAction,
  affirmativePending = false,
  alarming = false,
}: {
  part: AnyToolPart;
  copy: ApprovalCopy;
  notice?: ReactNode;
  primaryAction?: ApprovalPrimaryAction;
  /** Gate the affirmative button while the host works out which one it is. */
  affirmativePending?: boolean;
  alarming?: boolean;
}) {
  const { respond } = useContext(ToolHostContext);
  const approval = (part as { approval?: { id: string } }).approval;
  const [submitted, setSubmitted] = useState(false);
  const [running, setRunning] = useState(false);

  const answer = (approved: boolean) => {
    if (!respond || !approval || submitted) return;
    setSubmitted(true);
    // A failed POST re-enables the card so the member can retry.
    void Promise.resolve(respond(toolCallIdOf(part), approved)).catch(() =>
      setSubmitted(false),
    );
  };

  const runPrimary = async () => {
    if (!respond || !approval || submitted || running || !primaryAction) return;
    setRunning(true);
    try {
      if (await primaryAction.run()) answer(true);
    } finally {
      setRunning(false);
    }
  };

  const busy = primaryAction?.busy || running;

  return (
    <ToolNoticeCard
      icon={copy.icon}
      title={copy.title}
      detail={copy.detail}
      note={copy.note}
      notice={notice}
      alarming={alarming}
    >
      {respond && approval ? (
        <>
          {primaryAction ? (
            <Button
              type="button"
              size="sm"
              variant={primaryAction.variant}
              className="h-7 px-3"
              disabled={submitted || busy}
              onClick={() => void runPrimary()}
            >
              {busy ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                (primaryAction.icon ?? (
                  <ShieldCheck className="mr-1 h-3.5 w-3.5" />
                ))
              )}
              {primaryAction.label}
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              className="h-7 px-3"
              disabled={submitted || affirmativePending}
              onClick={() => answer(true)}
            >
              {affirmativePending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <ShieldCheck className="mr-1 h-3.5 w-3.5" />
              )}
              Approve
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-3"
            disabled={submitted || busy}
            onClick={() => answer(false)}
          >
            <ShieldX className="mr-1 h-3.5 w-3.5" />
            Deny
          </Button>
        </>
      ) : (
        <div className="text-[11px] italic text-muted-foreground">
          Waiting for a member to approve…
        </div>
      )}
    </ToolNoticeCard>
  );
}

function ToolResultImage({ image }: { image: ToolImage }) {
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );

  if (status === "error") {
    return (
      <div className="flex h-24 w-24 items-center justify-center rounded-md border border-border bg-muted/40 text-[10px] italic text-muted-foreground">
        image
      </div>
    );
  }

  return (
    <a
      href={image.src}
      target="_blank"
      rel="noreferrer"
      className="relative block max-h-48 overflow-hidden rounded-md border border-border bg-muted/40"
      title="Open full size"
    >
      {status === "loading" && (
        <div className="absolute inset-0 animate-pulse bg-muted" />
      )}
      <img
        src={image.src}
        alt="tool result"
        loading="lazy"
        decoding="async"
        draggable={false}
        onLoad={() => setStatus("ready")}
        onError={() => setStatus("error")}
        className="relative max-h-48 w-auto object-contain"
      />
    </a>
  );
}

function Section({
  label,
  content,
  tone,
  disclosed = false,
}: {
  label: string;
  content: string;
  tone?: "error";
  /** Start folded, with the label as the toggle. */
  disclosed?: boolean;
}) {
  const body = (
    <pre
      className={
        "mt-0.5 overflow-x-auto whitespace-pre-wrap break-words " +
        (tone === "error" ? "text-destructive" : "text-foreground")
      }
    >
      {content || "—"}
    </pre>
  );
  const heading = (
    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
      {label}
    </span>
  );
  if (!disclosed) {
    return (
      <div>
        <div>{heading}</div>
        {body}
      </div>
    );
  }
  return (
    <Collapsible>
      <CollapsibleTrigger className="group flex items-center gap-1 text-left hover:text-foreground">
        <ChevronRight className="h-2.5 w-2.5 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-90" />
        {heading}
      </CollapsibleTrigger>
      <CollapsibleContent>{body}</CollapsibleContent>
    </Collapsible>
  );
}
